import React from 'react';
import { Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';

/**
 * Empty-state hero card for the tool module (#97). Three variants:
 *
 *   - admin_no_tools:        first run for admin, catalog empty
 *   - tech_no_assignments:   technician with no active tools
 *   - filter_no_results:     filters/search returned no matches
 *
 * Each variant ships with a clear CTA so the user knows what to do next.
 */
const ToolEmptyState = ({
  variant,
  searchTerm = '',
  onPrimary,
  primaryLabel,
  onSecondary,
  secondaryLabel
}) => {
  const config = getConfig(variant, { searchTerm });

  return (
    <Card
      className="modern-card text-center"
      style={{ borderRadius: 16, padding: '2.5rem 1rem' }}
    >
      <div style={{ fontSize: '3rem', lineHeight: 1, opacity: 0.55 }}>
        <i className={`bi ${config.icon}`} />
      </div>
      <h5 className="mt-3 mb-2">{config.heading}</h5>
      <p className="text-muted mx-auto" style={{ maxWidth: 480 }}>
        {config.body}
      </p>
      <div className="d-flex flex-wrap justify-content-center gap-2 mt-3">
        {config.primary && (
          config.primary.to ? (
            <Button as={Link} to={config.primary.to} variant="primary">
              <i className={`bi ${config.primary.icon || 'bi-arrow-right'} me-1`} />
              {primaryLabel || config.primary.label}
            </Button>
          ) : (
            <Button variant="primary" onClick={onPrimary}>
              <i className={`bi ${config.primary.icon || 'bi-arrow-right'} me-1`} />
              {primaryLabel || config.primary.label}
            </Button>
          )
        )}
        {config.secondary && (
          config.secondary.to ? (
            <Button as={Link} to={config.secondary.to} variant="outline-secondary">
              <i className={`bi ${config.secondary.icon || 'bi-arrow-right'} me-1`} />
              {secondaryLabel || config.secondary.label}
            </Button>
          ) : (
            <Button variant="outline-secondary" onClick={onSecondary}>
              <i className={`bi ${config.secondary.icon || 'bi-arrow-right'} me-1`} />
              {secondaryLabel || config.secondary.label}
            </Button>
          )
        )}
      </div>
      {config.hint && (
        <small className="text-muted mt-3 d-block">{config.hint}</small>
      )}
    </Card>
  );
};

const getConfig = (variant, { searchTerm }) => {
  switch (variant) {
    case 'admin_no_tools':
      return {
        icon: 'bi-tools',
        heading: 'Aún no hay herramientas registradas',
        body: 'Registrá la primera herramienta para empezar a llevar control del inventario físico, asignarlas a técnicos y trackear su uso en obra.',
        primary: { label: 'Registrar primera herramienta', icon: 'bi-plus-lg' },
        secondary: { label: '¿Falta una categoría?', to: '/herramientas/categorias', icon: 'bi-tags' },
        hint: '¿Tenés muchas para importar? Podemos cargarlas desde Excel — pedile soporte.'
      };
    case 'tech_no_assignments':
      return {
        icon: 'bi-tools',
        heading: 'No tenés herramientas asignadas',
        body: 'Cuando un admin te asigne una o un jefe te pase una, aparecerá acá. Mientras tanto, podés ver qué hay disponible en bodega y tomar una.',
        primary: { label: 'Ver bodega', to: '/bodega', icon: 'bi-shop' }
      };
    case 'filter_no_results':
      return {
        icon: 'bi-search',
        heading: 'Sin resultados',
        body: searchTerm
          ? `Ninguna herramienta coincide con "${searchTerm}" y los filtros aplicados.`
          : 'Ninguna herramienta coincide con los filtros aplicados.',
        primary: { label: 'Limpiar filtros', icon: 'bi-x-circle' }
      };
    case 'warehouse_empty':
      return {
        icon: 'bi-shop',
        heading: 'Bodega vacía',
        body: 'Todas las herramientas están asignadas en este momento. Volvé cuando alguien devuelva una.',
        primary: { label: 'Ver mis herramientas', to: '/mis-herramientas', icon: 'bi-arrow-left' }
      };
    default:
      return {
        icon: 'bi-question-circle',
        heading: 'Sin contenido',
        body: ''
      };
  }
};

export default ToolEmptyState;
